import {
  Module,
  VuexModule,
  getModule,
  Action,
  MutationAction,
} from "vuex-module-decorators";
import store from "@/store";
import { showMessage } from "@/services/MessageService";
import { firestore, auth, storageRef, functions } from "@/config/firebase";
import { i18n } from "@/i18n";
@Module({
  dynamic: true,
  namespaced: true,
  name: "events",
  store,
})
class EventsModule extends VuexModule {
  /**
   * Register a new participant into an event
   * @param { import('@/models/events/users/ParticipantUser.model').ParticipantUser } userData Receives the participant information defined by the participant model at /models/events/users/ParticipantUser.model.ts
   */
  @Action
  async registerParticipant(userData) {
    let wrongPassword = false;
    try {
      if (userData.onlyRegisterInEventCollection) {
        delete userData.onlyRegisterInEventCollection;
        delete userData.password;
        await this.saveParticipantEventData(userData);
        store.dispatch("user/getEventDataExists", userData.eventID);
      } else {
        const userCredential = await auth.createUserWithEmailAndPassword(
          userData.email,
          userData.password
        );
        const user = userCredential.user;
        userData.uid = user!.uid;
        delete userData.password;

        await this.saveParticipantEventData(userData);
      }
    } catch (error) {
      if (error.code == "auth/email-already-in-use") {
        try {
          const userCredential = await auth.signInWithEmailAndPassword(
            userData.email,
            userData.password
          );
          const user = userCredential.user;
          userData.uid = user!.uid;
          delete userData.password;
          await this.saveParticipantEventData(userData);
        } catch (error) {
          if (error.code == "auth/wrong-password") {
            wrongPassword = true;
            showMessage({
              title: i18n.global.t("register.wrongPasswordError.title"),
              message: i18n.global.t("register.wrongPasswordError.message"),
              type: "error",
            });
          } else {
            showMessage({
              title: i18n.global.t("register.registerError.title"),
              message: i18n.global.t("register.registerError.message"),
              type: "error",
            });
          }
        }
      } else {
        showMessage({
          title: i18n.global.t("register.registerError.title"),
          message: i18n.global.t("register.registerError.message"),
          type: "error",
        });
      }
    }
    return wrongPassword;
  }
  /**
   * Saves participant data into database
   * @param { import('@/models/events/users/ParticipantUser.model').ParticipantUser } userData Receives the participant information defined by the participant model at /models/events/users/ParticipantUser.model.ts
   */
  @Action
  async saveParticipantEventData(userData) {
    try {
      if (userData.profileImage) {
        if (userData.profileImage.search("data:image") != -1) {
          const profileImage = await storageRef
            .child(
              `events/${userData.eventID}/participants/${userData.uid}/profileImage`
            )
            .putString(userData.profileImage, "data_url");
          const profileImageURL = await profileImage.ref.getDownloadURL();
          userData.profileImage = profileImageURL;
        }
      }
      if (userData.optionalFields) {
        for (let field = 0; field < userData.optionalFields.length; field++) {
          if (userData.optionalFields[field].type == "file") {
            delete userData.optionalFields[field].file;
            const fileRef = await storageRef
              .child(
                `events/${userData.eventID}/participants/${userData.uid}/${userData.optionalFields[field].id}`
              )
              .putString(userData.optionalFields[field].value, "data_url");
            const fileURL = await fileRef.ref.getDownloadURL();
            userData.optionalFields[field].value = fileURL;
          }
        }
      }

      userData.registerDate = new Date();
      await firestore("events")
        .doc(userData.eventID)
        .collection("participants")
        .doc(userData.uid)
        .set(userData);

      const addProduct = functions.httpsCallable("addProduct");
      await addProduct({
        eventId: userData.eventID,
        productDocumentPath: `events/${userData.eventID}/tickets/${userData.ticketType}`,
        quantity: 1,
      });
      showMessage({
        title: i18n.global.t("register.registerSuccess.title"),
        message: `${i18n.global.t("register.registerSuccess.message")} ${
          userData.eventName
        }`,
        type: "success",
      });
    } catch (error) {
      showMessage({
        title: i18n.global.t("register.registerError.title"),
        message: i18n.global.t("register.registerError.message"),
        type: "error",
      });
    }
  }
  /**
   * Delete the old ticket and updated the new ticket in the cart
   * @param {Object} ticketsData contains the eventID of the ticket, ticketToDelete is the old ticketType to delete and ticketToUpdate is the new ticketType
   */
  @Action
  async updateTicketParticipant(ticketsData) {
    try {
      const deleteCartItem = functions.httpsCallable("deleteCartItem");
      await deleteCartItem({
        eventId: ticketsData.eventID,
        productId: ticketsData.ticketToDelete,
      });
      const addProduct = functions.httpsCallable("addProduct");
      await addProduct({
        eventId: ticketsData.eventID,
        productDocumentPath: `events/${ticketsData.eventID}/tickets/${ticketsData.ticketToUpdate}`,
        quantity: 1,
        coupon: ticketsData.coupon,
      });
    } catch (error) {
      showMessage({
        title: i18n.global.t("register.selectTicketTypeError.title"),
        message: i18n.global.t("register.selectTicketTypeError.message"),
        type: "error",
      });
    }
  }

  /**
   * Get an event information from database
   * @param {string} eventID ID of the event that its going to be consulted from database
   * @returns {object} eventData information
   */
  @Action
  async getEventData(eventID) {
    const eventData = await firestore("events").doc(eventID).get();
    return eventData;
  }
}

export default getModule(EventsModule);
